import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import Layout from "../components/Layout";

const AuditSku = () => {
  const [sku] = useState("21008031");
  const [startDate] = useState("2026-04-01");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    runAudit();
  }, []);

  const runAudit = async () => {
    setLoading(true);
    try {
      // 1. MONTHLY AUDITOR: Scan every day in the current month for a SKU
      const monthStart = new Date().toISOString().slice(0, 8) + "01";
      const stockSnap = await getDocs(query(
          collection(db, "daily_stock"), 
          where("date", ">=", monthStart),
          orderBy("date", "asc")
      ));
      
      let dailyBreakdown = [];
      let totalMtdInward = 0;
      let totalMtdInvoice = 0;

      stockSnap.forEach(d => {
        const data = d.data();
        const inQty = Number(data.inwardQty || 0);
        const invQty = Number(data.invoiceQty || data.outwardQty || 0);
        
        totalMtdInward += inQty;
        totalMtdInvoice += invQty;

        dailyBreakdown.push({
            date: data.date,
            sku: data.sku,
            inward: inQty,
            invoice: invQty,
            docId: d.id
        });
      });

      setData({
        dailyBreakdown,
        totalMtdInward,
        totalMtdInvoice,
        found: dailyBreakdown.length > 0
      });

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetAllMTD = async () => {
    if (!window.confirm("Are you sure you want to delete ALL daily stock records for this entire month? This will reset all MTD totals to 0 for everyone.")) return;
    setIsDeleting(true);
    try {
        const { deleteDoc, doc } = await import("firebase/firestore");
        for (const item of data.dailyBreakdown) {
            await deleteDoc(doc(db, "daily_stock", item.docId));
        }
        alert("All Monthly Records Cleared. Refreshing...");
        runAudit();
    } catch (err) {
        alert("Error: " + err.message);
    } finally {
        setIsDeleting(false);
    }
  }

  if (loading || !data) return <Layout><div style={{ padding: "100px", textAlign: "center" }}>🔍 Building Monthly Stock Audit...</div></Layout>;

  return (
    <Layout>
      <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto", background: "#f8fafc", minHeight: "100vh" }}>
        <h1 style={{ color: "#1e293b", marginBottom: 8 }}>Monthly Stock Audit</h1>
        <p style={{ color: "#64748b", marginBottom: 32 }}>Breakdown for this month (Start: {new Date().toISOString().slice(0, 8) + "01"})</p>

        {data.found ? (
            <div style={{ background: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>SKU</th>
                            <th style={thStyle}>Inward</th>
                            <th style={thStyle}>Invoice</th>
                            <th style={thStyle}>Doc ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.dailyBreakdown.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                <td style={tdStyle}>{row.date}</td>
                                <td style={tdStyle}>{row.sku}</td>
                                <td style={tdStyle}>{row.inward}</td>
                                <td style={tdStyle}>{row.invoice}</td>
                                <td style={tdStyle}><code style={{ fontSize: 10 }}>{row.docId}</code></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: "#eff6ff", fontWeight: 800 }}>
                            <td colSpan={2} style={tdStyle}>MONTH TOTALS</td>
                            <td style={tdStyle}>{data.totalMtdInward}</td>
                            <td style={tdStyle}>{data.totalMtdInvoice}</td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        ) : (
            <div style={{ padding: "100px", textAlign: "center", background: "white", borderRadius: "12px" }}>
                <h2>No records found for this month yet.</h2>
                <button onClick={runAudit} style={{ padding: 10 }}>Rescan</button>
            </div>
        )}

        <div style={{ marginTop: 40, padding: 30, background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: "12px", textAlign: "center" }}>
            <p>💡 <strong>ULTIMATE RESET:</strong> If the Month-to-Date totals are completely wrong, you can click below to wipe the slate clean for this month and start closing from today.</p>
            <button 
                onClick={resetAllMTD}
                disabled={isDeleting}
                style={{ background: "#ef4444", color: "white", padding: "10px 20px", border: "none", borderRadius: "6px", cursor: isDeleting ? "not-allowed" : "pointer", fontWeight: 700 }}>
                {isDeleting ? "RESETTING..." : "WIPE ALL MONTHLY STOCK RECORDS"}
            </button>
        </div>
      </div>
    </Layout>
  );
};

const StatBox = ({ label, value, color }) => (
  <div style={{ background: "#ffffff", padding: "24px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
    <div style={{ fontSize: "12px", fontWeight: 800, color: "#64748b", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: "28px", fontWeight: 800, color }}>{value}</div>
  </div>
);

const thStyle = { padding: "12px 24px", fontSize: "11px", fontWeight: 800, color: "#64748b", textTransform: "uppercase" };
const tdStyle = { padding: "12px 24px", fontSize: "13px", color: "#1e293b" };

export default AuditSku;
